import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where, setDoc, doc, getDoc, updateDoc, runTransaction } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { generateTransactionReport } from '../../services/pdfReportService';
import VaultLogo from '../../assets/Vault.svg';
import './BulkTransactionEntry.css';

function BulkTransactionEntry({ onClose, mode }) {
  const [rows, setRows] = useState([createEmptyRow()]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);
  const user = auth.currentUser;

  // Convert SVG logo to PNG base64 with color inversion (white to black)
  useEffect(() => {
    const convertLogoToPngBase64 = async () => {
      try {
        const response = await fetch(VaultLogo);
        const svgText = await response.text();
        
        const img = new Image();
        const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 300;
          canvas.height = 300;
          
          const ctx = canvas.getContext('2d');
          
          // Draw the original SVG
          ctx.drawImage(img, 0, 0, 300, 300);
          
          // Get image data to invert colors
          const imageData = ctx.getImageData(0, 0, 300, 300);
          const data = imageData.data;
          
          // Invert colors: white becomes black, black becomes white
          for (let i = 0; i < data.length; i += 4) {
            data[i] = 255 - data[i];         // Red
            data[i + 1] = 255 - data[i + 1]; // Green
            data[i + 2] = 255 - data[i + 2]; // Blue
            // data[i + 3] is alpha, leave it unchanged
          }
          
          // Put inverted image back
          ctx.putImageData(imageData, 0, 0);
          
          // Convert to PNG base64
          const pngBase64 = canvas.toDataURL('image/png');
          setLogoBase64(pngBase64);
          
          URL.revokeObjectURL(url);
          console.log('✅ Logo inverted: white → black');
        };
        
        img.onerror = (err) => {
          URL.revokeObjectURL(url);
          console.warn('Could not load logo:', err);
        };
        
        img.src = url;
      } catch (error) {
        console.warn('Could not convert logo:', error);
      }
    };
    convertLogoToPngBase64();
  }, []);

  // Load accounts, categories, and accounting periods
  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        // Load ALL accounts (we'll filter by type when displaying)
        const accountsRef = collection(db, 'users', user.uid, 'account_setup');
        const accountsSnap = await getDocs(accountsRef);
        const accountsData = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAccounts(accountsData);

        // Load categories based on mode
        if (mode === 'expense-income') {
          const incomeRef = collection(db, 'users', user.uid, 'income_categories');
          const expenseRef = collection(db, 'users', user.uid, 'expense_categories');
          
          const [incomeSnap, expenseSnap] = await Promise.all([
            getDocs(incomeRef),
            getDocs(expenseRef)
          ]);
          
          const incomeData = incomeSnap.docs.map(doc => ({ ...doc.data(), categoryType: 'Income' }));
          const expenseData = expenseSnap.docs.map(doc => ({ ...doc.data(), categoryType: 'Expense' }));
          
          setCategories([...incomeData, ...expenseData]);
        }

        // Load accounting periods that are open
        const periodsRef = collection(db, 'users', user.uid, 'Accounting_Periods');
        const periodsQuery = query(periodsRef, where('isOpen', '==', true));
        const periodsSnap = await getDocs(periodsQuery);
        const periodsData = periodsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAccountingPeriods(periodsData);

      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [user, mode]);

  function createEmptyRow() {
    return {
      id: Date.now() + Math.random(),
      type: 'Expense',
      name: '',
      amount: '',
      category: '',
      account: '',
      trxDate: '',
      period: '',
      paid: true, // Default to checked
      refund: false,
      notes: '',
      repeat: 'Does not Repeat',
      ends: 'Does not Repeat'
    };
  }

  const addRow = () => {
    if (rows.length >= 10) {
      alert('⚠️ Maximum limit reached! You can only create up to 10 rows at a time.');
      return;
    }
    setRows([...rows, createEmptyRow()]);
  };

  const deleteRow = (index) => {
    if (rows.length > 1) {
      setRows(rows.filter((_, i) => i !== index));
    }
  };

  const duplicateRow = (index) => {
    const rowToDuplicate = { ...rows[index], id: Date.now() + Math.random() };
    const newRows = [...rows];
    newRows.splice(index + 1, 0, rowToDuplicate);
    setRows(newRows);
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    
    // If type changes, clear category and account as they depend on type
    if (field === 'type') {
      newRows[index].category = '';
      newRows[index].account = '';
      newRows[index].refund = false; // Reset refund when type changes
      newRows[index].amount = ''; // Clear amount to force re-entry with correct sign
    }
    
    setRows(newRows);
  };

  // Generate UUID for id field
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Generate random 8-character alphanumeric string
  const generateRandomString = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Convert DD-MM-YYYY to YYYY-MM (monthKey)
  const getMonthKeyFromPeriod = (periodDDMMYYYY) => {
    if (!periodDDMMYYYY) return '';
    const parts = periodDDMMYYYY.split('-');
    if (parts.length !== 3) return '';
    return `${parts[2]}-${parts[1]}`; // YYYY-MM
  };

  // Validate a single row
  const validateRow = (row, rowIndex) => {
    const errors = [];
    
    // Check required fields
    if (!row.type) errors.push(`Row ${rowIndex + 1}: Type is required`);
    if (!row.name || row.name.trim() === '') errors.push(`Row ${rowIndex + 1}: Name is required`);
    if (!row.amount || row.amount === '') errors.push(`Row ${rowIndex + 1}: Amount is required`);
    if (!row.category) errors.push(`Row ${rowIndex + 1}: Category is required`);
    if (!row.account) errors.push(`Row ${rowIndex + 1}: Account is required`);
    if (!row.trxDate) errors.push(`Row ${rowIndex + 1}: Transaction Date is required`);
    if (!row.period) errors.push(`Row ${rowIndex + 1}: Period is required`);
    
    // Validate amount based on type and refund
    const amount = parseFloat(row.amount);
    if (!isNaN(amount)) {
      if (row.type === 'Expense') {
        if (row.refund) {
          // Expense refund must be positive
          if (amount <= 0) {
            errors.push(`Row ${rowIndex + 1}: Expense refund amount must be positive`);
          }
        } else {
          // Regular expense must be negative
          if (amount >= 0) {
            errors.push(`Row ${rowIndex + 1}: Expense amount must be negative (use minus sign)`);
          }
        }
      } else if (row.type === 'Income') {
        // Income must always be positive
        if (amount <= 0) {
          errors.push(`Row ${rowIndex + 1}: Income amount must be positive`);
        }
      }
    }
    
    return errors;
  };

  // Validate all rows
  const validateAllRows = () => {
    const allErrors = [];
    rows.forEach((row, index) => {
      const rowErrors = validateRow(row, index);
      allErrors.push(...rowErrors);
    });
    return allErrors;
  };

  // TRANSACTION SAVING - ATOMIC (All or Nothing)
  const handleSave = async () => {
    // Prevent double-clicking
    if (saving) {
      console.log('⚠️ Save already in progress, ignoring duplicate click');
      return;
    }
    
    try {
      setSaving(true);
      
      // Validate all rows
      const validationErrors = validateAllRows();
      if (validationErrors.length > 0) {
        alert('Please fix the following errors:\n\n' + validationErrors.join('\n'));
        setSaving(false);
        return;
      }

      // Prepare all transactions and calculate balance deltas BEFORE committing
      const transactionsToSave = [];
      const balanceDeltas = {}; // { accountName: totalDelta }

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        
        // Get monthKey from period (DD-MM-YYYY -> YYYY-MM)
        const monthKey = getMonthKeyFromPeriod(row.period);
        if (!monthKey) {
          throw new Error(`Row ${i + 1}: Invalid period format`);
        }

        // Generate docId based on type
        const randomStr = generateRandomString();
        const docId = row.type === 'Expense' 
          ? `EXP-${monthKey}-${randomStr}`
          : `INC-${monthKey}-${randomStr}`;

        // Generate UUID for id field
        const uuid = generateUUID();

        // Prepare notes with prefix
        const notesPrefix = `${monthKey} - WebAPP upload - `;
        const finalNotes = notesPrefix + (row.notes || '');

        // Prepare transaction document
        const transactionData = {
          id: uuid,
          type: row.type,
          name: row.name,
          amount: parseFloat(row.amount),
          category: row.category,
          account: row.account,
          date: row.trxDate, // DD-MM-YYYY
          period: row.period, // DD-MM-YYYY
          paid: row.paid,
          split: row.refund, // Map refund checkbox to split field
          notes: finalNotes,
          repeat: row.repeat,
          ends: row.ends,
          installmentId: '',
          isInstallment: false,
          reflectFrom: false,
          reflectTo: false,
          status: 'Validated & Uploaded' // For report
        };

        // Store for atomic save
        transactionsToSave.push({
          monthKey,
          docId,
          data: transactionData,
          rowIndex: i + 1,
          rowName: row.name
        });

        // Track balance delta if paid is true
        if (row.paid) {
          const amount = parseFloat(row.amount);
          if (!balanceDeltas[row.account]) {
            balanceDeltas[row.account] = 0;
          }
          balanceDeltas[row.account] += amount;
        }
      }

      // Execute ATOMIC transaction - ALL OR NOTHING
      await runTransaction(db, async (transaction) => {
        // 1. READ PHASE - Get current balances (if any paid transactions exist)
        let currentBalances = {};
        if (Object.keys(balanceDeltas).length > 0) {
          const balancesRef = doc(db, 'users', user.uid, 'balances', 'balances');
          const balancesSnap = await transaction.get(balancesRef);
          
          if (balancesSnap.exists()) {
            currentBalances = balancesSnap.data();
          }
        }

        // 2. WRITE PHASE - Save all transactions
        for (const txn of transactionsToSave) {
          const docRef = doc(db, 'users', user.uid, 'transactions', txn.monthKey, 'items', txn.docId);
          transaction.set(docRef, txn.data);
        }

        // 3. WRITE PHASE - Update balances (incremental)
        if (Object.keys(balanceDeltas).length > 0) {
          const balancesRef = doc(db, 'users', user.uid, 'balances', 'balances');
          
          // Calculate new balances (incremental update)
          const updatedBalances = { ...currentBalances };
          for (const accountName in balanceDeltas) {
            const oldBalance = currentBalances[accountName] || 0;
            const delta = balanceDeltas[accountName];
            updatedBalances[accountName] = oldBalance + delta;
          }

          // Update or set balances document
          if (Object.keys(currentBalances).length > 0) {
            transaction.update(balancesRef, updatedBalances);
          } else {
            transaction.set(balancesRef, updatedBalances);
          }

          console.log('✅ Balance deltas:', balanceDeltas);
          console.log('✅ New balances:', updatedBalances);
        }
      });

      // SUCCESS - Ask if user wants to generate report
      console.log('✅ Transactions saved successfully');
      
      const paidCount = rows.filter(r => r.paid).length;
      const unpaidCount = rows.length - paidCount;
      const balanceMsg = paidCount > 0 
        ? `\n💰 Updated balances for ${Object.keys(balanceDeltas).length} account(s)` 
        : '';
      const unpaidMsg = unpaidCount > 0 
        ? `\n⏳ ${unpaidCount} unpaid transaction(s) saved (balances not affected)` 
        : '';
      
      // Ask if user wants to generate report
      const generateReport = window.confirm(
        `✅ SUCCESS! All transactions saved!\n\n` +
        `📝 Saved: ${rows.length} transaction(s)` +
        balanceMsg +
        unpaidMsg +
        `\n\n📄 Would you like to generate a PDF report?\n\n` +
        `Click OK to generate report, or Cancel to skip.`
      );

      if (generateReport) {
        try {
          console.log('📄 Generating report...');
          const transactionDataForReport = transactionsToSave.map(txn => txn.data);
          const reportInfo = await generateTransactionReport(user.uid, transactionDataForReport, balanceDeltas, logoBase64);
          
          alert(
            `✅ Report generated successfully!\n\n` +
            `📄 ${reportInfo.filename}\n` +
            `📁 Saved to: File Cabinet > Reports > Transactions`
          );
        } catch (reportError) {
          console.error('Error generating report:', reportError);
          alert(
            `⚠️ Transactions saved, but report generation failed:\n\n${reportError.message}\n\n` +
            `You can try generating the report again later.`
          );
        }
      }
      
      setSaving(false);
      onClose();

    } catch (error) {
      console.error('❌ Transaction failed - ALL CHANGES ROLLED BACK:', error);
      
      let errorMessage = 'Transaction failed and all changes were rolled back. ';
      
      // Provide helpful error messages
      if (error.code === 'failed-precondition') {
        errorMessage += 'Too many operations. Please reduce the number of rows.';
      } else if (error.code === 'aborted') {
        errorMessage += 'Transaction aborted due to conflict. Please try again.';
      } else if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check your access rights.';
      } else {
        errorMessage += error.message;
      }
      
      alert('❌ ERROR: ' + errorMessage);
      setSaving(false);
    }
  };

  // DRAFT SAVING - Save to users/[uid]/Settings/WebPage/Drafts
  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      
      const draftData = {
        mode: mode,
        rows: rows,
        rowCount: rows.length,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Save to users/[uid]/Settings/WebPage/Drafts
      const draftsRef = collection(db, 'users', user.uid, 'Settings', 'WebPage', 'Drafts');
      await addDoc(draftsRef, draftData);

      alert(`Draft saved successfully with ${rows.length} row(s)! You can resume editing later.`);
      setSaving(false);
      onClose();
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft: ' + error.message);
      setSaving(false);
    }
  };

  // Get categories filtered by transaction type
  const getCategoriesForType = (type) => {
    return categories.filter(cat => cat.categoryType === type);
  };

  // Get accounts filtered by type (active + allowExpense/allowIncome)
  const getAccountsForType = (type) => {
    return accounts.filter(acc => {
      if (!acc.active) return false;
      
      if (type === 'Expense') {
        return acc.allowExpense === true;
      } else if (type === 'Income') {
        return acc.allowIncome === true;
      }
      
      return false;
    });
  };

  // Convert YYYY-MM-DD to DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Convert DD-MM-YYYY to YYYY-MM-DD for date input
  const formatDateToYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Check if a date is within open accounting periods
  const isDateInOpenPeriod = (dateStr) => {
    if (!dateStr) return false;
    
    // Convert DD-MM-YYYY to Date object
    const parts = dateStr.split('-');
    if (parts.length !== 3) return false;
    
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
    const year = parseInt(parts[2]);
    const checkDate = new Date(year, month, day);
    
    // Check if date falls within any open period's month
    return accountingPeriods.some(period => {
      const periodParts = period.date.split('-'); // YYYY-MM-DD
      const periodYear = parseInt(periodParts[0]);
      const periodMonth = parseInt(periodParts[1]) - 1;
      
      // Get first and last day of the period month
      const firstDay = new Date(periodYear, periodMonth, 1);
      const lastDay = new Date(periodYear, periodMonth + 1, 0);
      
      return checkDate >= firstDay && checkDate <= lastDay;
    });
  };

  // Handle transaction date change with validation
  const handleTrxDateChange = (index, value) => {
    // value is in YYYY-MM-DD format from date input
    const ddmmyyyy = formatDateToDDMMYYYY(value);
    
    if (isDateInOpenPeriod(ddmmyyyy)) {
      updateRow(index, 'trxDate', ddmmyyyy);
    } else {
      alert('Transaction date must be within an open accounting period month.');
      // Clear the invalid date
      updateRow(index, 'trxDate', '');
    }
  };

  // Handle amount change with validation
  const handleAmountChange = (index, value) => {
    const row = rows[index];
    const amount = parseFloat(value);
    
    // Allow empty input for editing
    if (value === '' || value === '-') {
      updateRow(index, 'amount', value);
      return;
    }
    
    // Check if it's a valid number
    if (isNaN(amount)) {
      return; // Don't update if invalid
    }

    // Validate based on type and refund
    if (row.type === 'Expense') {
      if (row.refund) {
        // Expense refund must be positive
        if (amount > 0) {
          updateRow(index, 'amount', value);
        } else {
          alert('Expense refund amount must be positive');
        }
      } else {
        // Regular expense must be negative
        if (amount < 0) {
          updateRow(index, 'amount', value);
        } else {
          alert('Expense amount must be negative. Please use a minus sign (e.g., -50.00)');
        }
      }
    } else if (row.type === 'Income') {
      // Income must always be positive
      if (amount > 0) {
        updateRow(index, 'amount', value);
      } else {
        alert('Income amount must be positive');
      }
    }
  };

  // Handle refund checkbox change
  const handleRefundChange = (index, checked) => {
    const row = rows[index];
    updateRow(index, 'refund', checked);
    
    // Clear amount to force re-entry with correct sign
    if (row.amount) {
      updateRow(index, 'amount', '');
      alert('Please re-enter the amount with the correct sign based on refund status');
    }
  };

  // Calculate total amount
  const calculateTotal = () => {
    return rows.reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);
  };

  // For Expense/Income mode only
  if (mode !== 'expense-income') {
    return <div>Transfer/Payment mode not yet implemented</div>;
  }

  return (
    <div className="bulk-transaction-modal">
      <div className="bulk-transaction-content">
        <div className="bulk-header">
          <h2>Bulk Transaction Entry - Expense/Income</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="bulk-toolbar">
          <button className="btn-action" onClick={addRow} disabled={rows.length >= 10}>
            ➕ Add Row {rows.length >= 10 ? '(Max 10)' : ''}
          </button>
          <button className="btn-action" style={{background: 'linear-gradient(135deg, #ff7961, #ff6b9d)'}} onClick={handleSaveDraft} disabled={saving}>
            {saving ? '💾 Saving Draft...' : '📝 Save as Draft'}
          </button>
          <button className="btn-action" onClick={handleSave} disabled={saving}>
            {saving ? '💾 Saving...' : '💾 Save All Transactions'}
          </button>
          <span className="row-count">{rows.length}/10 row(s) | Total: £{calculateTotal().toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>

        <div className="table-container">
          <table className="bulk-table">
            <thead>
              <tr>
                <th className="sticky-col">#</th>
                <th>Type *</th>
                <th>Name *</th>
                <th>Amount *</th>
                <th>Category *</th>
                <th>Account *</th>
                <th>Trx Date *</th>
                <th>Period *</th>
                <th>Paid *</th>
                <th>Refund *</th>
                <th>Notes</th>
                <th>Repeat</th>
                <th>Ends</th>
                <th className="sticky-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="sticky-col">{index + 1}</td>
                  
                  <td>
                    <select
                      value={row.type}
                      onChange={(e) => updateRow(index, 'type', e.target.value)}
                      className="required"
                    >
                      <option value="Expense">Expense</option>
                      <option value="Income">Income</option>
                    </select>
                  </td>
                  
                  <td>
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => updateRow(index, 'name', e.target.value)}
                      placeholder="Transaction name"
                      className="required"
                    />
                  </td>
                  
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={row.amount}
                      onChange={(e) => handleAmountChange(index, e.target.value)}
                      placeholder={row.type === 'Expense' && !row.refund ? '-0.00' : '0.00'}
                      className="required"
                      title={
                        row.type === 'Expense' 
                          ? (row.refund ? 'Enter positive amount for refund' : 'Enter negative amount (e.g., -50.00)')
                          : 'Enter positive amount'
                      }
                    />
                  </td>
                  
                  <td>
                    <select
                      value={row.category}
                      onChange={(e) => updateRow(index, 'category', e.target.value)}
                      className="required"
                    >
                      <option value="">Select...</option>
                      {getCategoriesForType(row.type).map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </td>
                  
                  <td>
                    <select
                      value={row.account}
                      onChange={(e) => updateRow(index, 'account', e.target.value)}
                      className="required"
                    >
                      <option value="">Select...</option>
                      {getAccountsForType(row.type).map(acc => (
                        <option key={acc.id} value={acc.name}>{acc.name}</option>
                      ))}
                    </select>
                  </td>
                  
                  <td>
                    <input
                      type="date"
                      value={formatDateToYYYYMMDD(row.trxDate)}
                      onChange={(e) => handleTrxDateChange(index, e.target.value)}
                      className="required"
                      placeholder="DD-MM-YYYY"
                    />
                  </td>
                  
                  <td>
                    <select
                      value={row.period}
                      onChange={(e) => updateRow(index, 'period', e.target.value)}
                      className="required"
                    >
                      <option value="">Select...</option>
                      {accountingPeriods.map(period => (
                        <option key={period.id} value={formatDateToDDMMYYYY(period.date)}>
                          {formatDateToDDMMYYYY(period.date)}
                        </option>
                      ))}
                    </select>
                  </td>
                  
                  <td className="center">
                    <input
                      type="checkbox"
                      checked={row.paid}
                      onChange={(e) => updateRow(index, 'paid', e.target.checked)}
                    />
                  </td>
                  
                  <td className="center">
                    <input
                      type="checkbox"
                      checked={row.refund}
                      onChange={(e) => handleRefundChange(index, e.target.checked)}
                      disabled={row.type === 'Income'}
                      title={row.type === 'Income' ? 'Refund not available for Income' : 'Check if this is a refund'}
                    />
                  </td>
                  
                  <td>
                    <input
                      type="text"
                      value={row.notes}
                      onChange={(e) => updateRow(index, 'notes', e.target.value)}
                      placeholder="Notes"
                    />
                  </td>
                  
                  <td>
                    <select
                      value={row.repeat}
                      onChange={(e) => updateRow(index, 'repeat', e.target.value)}
                    >
                      <option value="Does not Repeat">Does not Repeat</option>
                    </select>
                  </td>
                  
                  <td>
                    <select
                      value={row.ends}
                      onChange={(e) => updateRow(index, 'ends', e.target.value)}
                    >
                      <option value="Does not Repeat">Does not Repeat</option>
                    </select>
                  </td>
                  
                  <td className="sticky-actions">
                    <button className="btn-icon" onClick={() => duplicateRow(index)} title="Duplicate">📋</button>
                    <button className="btn-icon" onClick={() => deleteRow(index)} title="Delete" disabled={rows.length === 1}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td className="sticky-col"></td>
                <td colSpan="2"><strong>TOTAL</strong></td>
                <td><strong>£{calculateTotal().toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                <td colSpan="9"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bulk-footer">
          <p className="help-text">
            * Required fields | Use Tab to navigate | Dates in DD-MM-YYYY format | 
            <strong> EXPENSES: Use negative amounts (e.g., -50.00) unless it's a refund</strong> | 
            <strong> INCOME: Always positive amounts</strong>
          </p>
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-draft" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving Draft...' : '📝 Save as Draft'}
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving Transactions...' : '💾 Save All Transactions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkTransactionEntry;